import React, { useEffect, useState } from 'react';
import { Badge, Button, Col, Row, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Section from 'components/common/Section';
import SectionHeader from './SectionHeader';
import { useTranslation } from 'react-i18next';
import { fetchPublicPlans } from 'helpers/plans';

const CURRENCY_MAP = {
  AR: { key: 'price_ars', symbol: '$', suffix: 'ARS' },
  BR: { key: 'price_brl', symbol: 'R$', suffix: 'BRL' },
  US: { key: 'price_usd', symbol: '$', suffix: 'USD' },
  CL: { key: 'price_clp', symbol: '$', suffix: 'CLP' },
  CN: { key: 'price_cny', symbol: '¥', suffix: 'CNY' },
};

const USD_CURRENCY = { key: 'price_usd', symbol: '$', suffix: 'USD' };

const LANG_MAP = { en: 'en', es: 'es', pt: 'br', 'pt-BR': 'br', zh: 'zh' };

const FEATURE_CHECKS = [
  { key: 'max_questions_per_month', labelKey: 'PRICING_FEAT_QUESTIONS', format: v => v === 0 ? '∞' : v },
  { key: 'max_ml_accounts', labelKey: 'PRICING_FEAT_ML_ACCOUNTS', format: v => v },
  { key: 'max_users', labelKey: 'PRICING_FEAT_USERS', format: v => v },
  { key: 'max_agents', labelKey: 'PRICING_FEAT_AGENTS', format: v => v },
  { key: 'max_agent_docs_per_product', labelKey: 'PRICING_FEAT_AGENT_DOCS', format: v => v },
  { key: 'data_retention_days', labelKey: 'PRICING_FEAT_RETENTION', format: v => v === 0 ? '∞' : `${v}d` },
];

const BOOLEAN_FEATURES = [
  { key: 'has_analytics', labelKey: 'PRICING_FEAT_ANALYTICS' },
  { key: 'has_stock_module', labelKey: 'PRICING_FEAT_STOCK' },
  { key: 'has_claims_module', labelKey: 'PRICING_FEAT_CLAIMS' },
  { key: 'has_calendar_module', labelKey: 'PRICING_FEAT_CALENDAR' },
  { key: 'has_whatsapp', labelKey: 'PRICING_FEAT_WHATSAPP' },
  { key: 'has_agent_customization', labelKey: 'PRICING_FEAT_CUSTOMIZATION' },
  { key: 'has_priority_support', labelKey: 'PRICING_FEAT_PRIORITY' },
];

const LandingPricing = () => {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState('monthly');
  const [country, setCountry] = useState('US');

  useEffect(() => {
    fetchPublicPlans()
      .then(data => setPlans(data.plans || []))
      .catch(() => {});
  }, []);

  // Detect country from navigator language
  useEffect(() => {
    const lang = navigator.language || '';
    if (lang.includes('AR') || lang.includes('es-AR')) setCountry('AR');
    else if (lang.includes('BR') || lang.includes('pt')) setCountry('BR');
    else if (lang.includes('CL') || lang.includes('es-CL')) setCountry('CL');
    else if (lang.includes('zh') || lang.includes('CN')) setCountry('CN');
  }, []);

  // Resolve localized text helper
  const langCode = LANG_MAP[i18n.language] || LANG_MAP[i18n.language?.split('-')[0]] || 'en';
  const localizedName = (plan) =>
    plan[`name_${langCode}`] || plan.name_en || plan.name;
  const localizedDesc = (plan) =>
    plan[`description_${langCode}`] || plan.description_en || plan.description;

  const localCurrency = CURRENCY_MAP[country] || CURRENCY_MAP.US;

  // Check if any plan has yearly enabled
  const hasYearly = plans.some(p => p.enable_yearly);

  /**
   * Calculate the per-month price for yearly billing.
   * If yearly_price_usd_override > 0, use that directly as the per-month price.
   * Otherwise, apply yearly_discount_pct to the monthly price.
   */
  const getYearlyMonthlyUsd = (plan) => {
    const override = parseFloat(plan.yearly_price_usd_override || 0);
    if (override > 0) return override;
    const monthly = parseFloat(plan.price_usd || 0);
    const discount = parseFloat(plan.yearly_discount_pct || 0);
    return monthly * (1 - discount / 100);
  };

  const getYearlyMonthlyLocal = (plan, currencyKey) => {
    // For local currencies we apply the same discount percentage
    const monthly = parseFloat(plan[currencyKey] || 0);
    const discount = parseFloat(plan.yearly_discount_pct || 0);
    return monthly * (1 - discount / 100);
  };

  if (plans.length === 0) return null;

  return (
    <Section bg="light" className="text-center py-6" id="pricing">
      <SectionHeader
        title={t('LANDING.PRICING_TITLE')}
        subtitle={t('LANDING.PRICING_SUBTITLE')}
      />

      {/* Billing toggle */}
      {hasYearly && (
        <div className="d-flex justify-content-center mt-4 mb-2">
          <ToggleButtonGroup
            type="radio"
            name="billing"
            value={billing}
            onChange={v => setBilling(v)}
          >
            <ToggleButton
              id="billing-monthly"
              variant={billing === 'monthly' ? 'primary' : 'outline-primary'}
              value="monthly"
              className="px-4"
            >
              {t('LANDING.PRICING_MONTHLY')}
            </ToggleButton>
            <ToggleButton
              id="billing-yearly"
              variant={billing === 'yearly' ? 'primary' : 'outline-primary'}
              value="yearly"
              className="px-4"
            >
              {t('LANDING.PRICING_YEARLY')}
              <Badge bg="success" className="ms-2">{t('LANDING.PRICING_SAVE')}</Badge>
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      )}

      <Row className="g-4 mt-3 justify-content-center">
        {plans.map((plan, index) => {
          const pricingDisplay = plan.pricing_display || {};
          const countryDisplay = pricingDisplay[country] || { show_usd: true, show_local: false };
          const showUsd = countryDisplay.show_usd !== false;
          const showLocal = countryDisplay.show_local === true && country !== 'US';

          const isYearly = billing === 'yearly' && plan.enable_yearly;

          const usdMonthly = parseFloat(plan.price_usd || 0);
          const usdPrice = isYearly ? getYearlyMonthlyUsd(plan) : usdMonthly;
          const localPrice = isYearly
            ? getYearlyMonthlyLocal(plan, localCurrency.key)
            : parseFloat(plan[localCurrency.key] || 0);

          const isFeatured = index === 1 || plan.slug === 'professional';
          const isEnterprise = plan.slug === 'enterprise';

          return (
            <Col key={plan.slug} lg={3} md={6} sm={12}>
              <div
                className={`card h-100 shadow-sm border-0 ${isFeatured ? 'border-primary border-2 shadow' : ''}`}
                style={{
                  borderRadius: '1rem',
                  ...(isFeatured ? { border: '2px solid var(--falcon-primary)', transform: 'scale(1.03)' } : {}),
                }}
              >
                {isFeatured && (
                  <div
                    className="bg-primary text-white text-center py-1 fw-bold"
                    style={{ borderRadius: '1rem 1rem 0 0', fontSize: '0.75rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}
                  >
                    {t('LANDING.PRICING_POPULAR')}
                  </div>
                )}
                <div className="card-body d-flex flex-column p-4">
                  <h4 className="fw-bold mb-1">{localizedName(plan)}</h4>
                  <p className="text-muted small mb-3">{localizedDesc(plan)}</p>

                  {/* Price — always per month */}
                  <div className="mb-4">
                    {isEnterprise ? (
                      <h2 className="fw-bold mb-0">{t('LANDING.PRICING_CUSTOM')}</h2>
                    ) : (
                      <>
                        {/* Primary price: USD if show_usd, else local */}
                        {showUsd && (
                          <h2 className="fw-bold mb-0">
                            <span className="fs-2 align-top">{USD_CURRENCY.symbol}</span>
                            {usdPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            <small className="text-muted fs--1 fw-normal">
                              {' '}{USD_CURRENCY.suffix}/{t('LANDING.PRICING_MONTH')}
                            </small>
                          </h2>
                        )}
                        {/* Secondary local price (smaller) */}
                        {showLocal && localPrice > 0 && (
                          <p className={`${showUsd ? 'text-muted small' : 'fw-bold fs-2'} mb-0`}>
                            {!showUsd && <span className="align-top">{localCurrency.symbol}</span>}
                            {showUsd ? `≈ ${localCurrency.symbol}` : ''}
                            {localPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            <small className={showUsd ? '' : 'text-muted fs--1 fw-normal'}>
                              {' '}{localCurrency.suffix}/{t('LANDING.PRICING_MONTH')}
                            </small>
                          </p>
                        )}
                        {/* Fallback if neither flag is set */}
                        {!showUsd && !showLocal && (
                          <h2 className="fw-bold mb-0">
                            <span className="fs-2 align-top">{USD_CURRENCY.symbol}</span>
                            {usdPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            <small className="text-muted fs--1 fw-normal">
                              {' '}{USD_CURRENCY.suffix}/{t('LANDING.PRICING_MONTH')}
                            </small>
                          </h2>
                        )}
                        {/* "Billed annually" note when yearly is selected */}
                        {isYearly && (
                          <p className="text-muted small mb-0 mt-1">
                            {t('LANDING.PRICING_BILLED_ANNUALLY')}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {/* CTA */}
                  <Button
                    as={Link}
                    to={isEnterprise ? '/waitlist' : '/login'}
                    variant={isFeatured ? 'primary' : 'outline-primary'}
                    className="w-100 rounded-pill mb-4 fw-semi-bold"
                  >
                    {isEnterprise ? t('LANDING.PRICING_CONTACT') : t('LANDING.PRICING_GET_STARTED')}
                  </Button>

                  {/* Features */}
                  <ul className="list-unstyled text-start flex-grow-1">
                    {FEATURE_CHECKS.map(feat => (
                      <li key={feat.key} className="py-1 d-flex align-items-start">
                        <FontAwesomeIcon icon="check" className="text-success me-2 mt-1 flex-shrink-0" />
                        <span>
                          <strong>{feat.format(plan[feat.key])}</strong> {t(`LANDING.${feat.labelKey}`)}
                        </span>
                      </li>
                    ))}
                    {BOOLEAN_FEATURES.map(feat => (
                      <li key={feat.key} className="py-1 d-flex align-items-start">
                        {plan[feat.key] ? (
                          <FontAwesomeIcon icon="check" className="text-success me-2 mt-1 flex-shrink-0" />
                        ) : (
                          <FontAwesomeIcon icon="times" className="text-300 me-2 mt-1 flex-shrink-0" />
                        )}
                        <span className={plan[feat.key] ? '' : 'text-400'}>
                          {t(`LANDING.${feat.labelKey}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </Section>
  );
};

export default LandingPricing;
